import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { fmtMoney, fmtPct, fmtNum } from "../lib/exports";
import { Card } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, AreaChart, Area,
} from "recharts";
import { TrendingUp, TrendingDown, DollarSign, ShoppingBag, Drumstick, Receipt, Percent, Target } from "lucide-react";

const KPI = ({ label, value, sub, positive, icon: Icon, testid }) => (
  <Card className="p-5 bg-white border border-slate-200 hover:border-slate-300 transition-colors" data-testid={testid}>
    <div className="flex items-start justify-between">
      <div className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">{label}</div>
      <div className="w-8 h-8 rounded-md bg-slate-100 flex items-center justify-center text-slate-600">
        <Icon className="w-4 h-4" />
      </div>
    </div>
    <div className={`mt-3 text-3xl font-bold tracking-tight ${
      positive === true ? "text-emerald-600" : positive === false ? "text-red-600" : "text-slate-900"
    }`}>{value}</div>
    {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
  </Card>
);

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [sucursales, setSucursales] = useState([]);
  const [sucursalId, setSucursalId] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/sucursales").then((r) => setSucursales(r.data));
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = sucursalId && sucursalId !== "all" ? { sucursal_id: sucursalId } : {};
    api.get("/reportes/dashboard", { params })
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [sucursalId]);

  const hoy = data?.hoy || {};
  const mes = data?.mes || {};

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.15em] text-blue-600">Panel principal</div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mt-1">Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">Resumen financiero en tiempo real.</p>
        </div>
        <div className="w-full sm:w-56">
          <Select value={sucursalId || "all"} onValueChange={(v) => setSucursalId(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="dashboard-sucursal" className="bg-blue-50 border-blue-200 text-blue-900">
              <SelectValue placeholder="Todas las sucursales" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las sucursales</SelectItem>
              {sucursales.map((s) => (
                <SelectItem key={s.id} value={s.id}>{s.nombre}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading && <div className="text-sm text-slate-500">Cargando…</div>}

      {!loading && data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
            <KPI testid="kpi-ventas-hoy" label="Ventas Hoy" icon={DollarSign} value={fmtMoney(hoy.ventas)} sub={`${hoy.tickets || 0} tickets`} />
            <KPI testid="kpi-utilidad-hoy" label="Utilidad Hoy" icon={TrendingUp} value={fmtMoney(hoy.utilidad_neta)} positive={hoy.utilidad_neta >= 0} sub={fmtPct(hoy.margen_pct) + " margen"} />
            <KPI testid="kpi-ventas-mes" label="Ventas Mes" icon={ShoppingBag} value={fmtMoney(mes.ventas)} sub={`${mes.tickets || 0} tickets`} />
            <KPI testid="kpi-utilidad-mes" label="Utilidad Mes" icon={TrendingUp} value={fmtMoney(mes.utilidad_neta)} positive={mes.utilidad_neta >= 0} sub={fmtPct(mes.margen_pct) + " margen"} />
            <KPI testid="kpi-food-cost" label="Food Cost %" icon={Percent} value={fmtPct(mes.food_cost_pct)} positive={mes.food_cost_pct <= 40 ? true : mes.food_cost_pct >= 50 ? false : undefined} sub="Objetivo < 40%" />
            <KPI testid="kpi-ganancia-pollo" label="Ganancia por Pollo" icon={Drumstick} value={fmtMoney(mes.ganancia_por_pollo)} positive={mes.ganancia_por_pollo >= 0} sub="Promedio del mes" />
            <KPI testid="kpi-pollos-eq" label="Pollos Equivalentes" icon={Target} value={fmtNum(mes.pollos_equivalentes, 0)} sub="Vendidos este mes" />
            <KPI testid="kpi-ticket-prom" label="Ticket Promedio" icon={Receipt} value={fmtMoney(mes.ticket_promedio)} sub="Mes en curso" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card className="p-5 lg:col-span-2" data-testid="chart-tendencia">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Últimos 14 días</div>
                  <h3 className="text-lg font-semibold">Tendencia de ventas y utilidad</h3>
                </div>
              </div>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={data.tendencia}>
                    <defs>
                      <linearGradient id="gV" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#2563eb" stopOpacity={0.3} />
                        <stop offset="100%" stopColor="#2563eb" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="gU" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#10b981" stopOpacity={0.3} />
                        <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="fecha" stroke="#64748b" fontSize={10} tickFormatter={(d) => d.slice(5)} />
                    <YAxis stroke="#64748b" fontSize={10} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v) => fmtMoney(v)} contentStyle={{ background: "#1e293b", border: "none", borderRadius: 6, color: "white" }} />
                    <Area type="monotone" dataKey="ventas" stroke="#2563eb" fill="url(#gV)" strokeWidth={2} name="Ventas" />
                    <Area type="monotone" dataKey="utilidad" stroke="#10b981" fill="url(#gU)" strokeWidth={2} name="Utilidad" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </Card>

            <Card className="p-5 bg-slate-900 text-white" data-testid="card-resumen-hoy">
              <div className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-400 mb-3">Resumen de hoy</div>
              <div className="space-y-3 font-mono text-sm">
                <Row label="Ventas" value={fmtMoney(hoy.ventas)} />
                <Row label="(-) Costo de venta" value={fmtMoney(hoy.costo_venta)} />
                <Row label="= Utilidad bruta" value={fmtMoney(hoy.utilidad_bruta)} bold positive={hoy.utilidad_bruta >= 0} />
                <Row label="(-) Gastos operativos" value={fmtMoney(hoy.gastos_operativos)} />
                <Row label="= Utilidad neta" value={fmtMoney(hoy.utilidad_neta)} bold positive={hoy.utilidad_neta >= 0} />
                <div className="border-t border-slate-700 pt-3 mt-3 space-y-2">
                  <Row label="Food Cost" value={fmtPct(hoy.food_cost_pct)} />
                  <Row label="Margen Neto" value={fmtPct(hoy.margen_pct)} positive={hoy.margen_pct >= 0} />
                  <Row label="Pollos Eq." value={fmtNum(hoy.pollos_equivalentes, 1)} />
                </div>
              </div>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}

const Row = ({ label, value, bold, positive }) => (
  <div className="flex justify-between items-center">
    <span className={`text-slate-300 ${bold ? "font-semibold text-white" : ""}`}>{label}</span>
    <span className={`${bold ? "font-bold text-lg" : ""} ${
      positive === true ? "text-emerald-400" : positive === false ? "text-red-400" : "text-white"
    }`}>{value}</span>
  </div>
);
